/**
 * Purpose: Renders answer option buttons — 2x2 grid for multiple choice, centered pair for true/false.
 * Owns: Answer button layout, selection handling, correct/incorrect visual feedback, 3D depth.
 * Notes: Each button uses a distinct theme color (A=dusty red, B=golden yellow,
 *        C=forest green, D=slate blue). Large tap targets (min 48px) per accessibility
 *        requirements. Buttons use CSS gradient + box-shadow for tactile 3D depth,
 *        with a pressed state that shifts the shadow (classes from chalkboard-textures.css).
 */

import type { QuestionType } from '../../types/question';

interface AnswerButtonsProps {
  /** The answer options to display */
  options: string[];
  /** "multiple-choice" for 4 buttons in 2x2, "true-false" for 2 centered buttons */
  questionType: QuestionType;
  /** Index of the answer the player selected (null if not yet answered) */
  selectedAnswer: number | null;
  /** Index of the correct answer (shown after selection for feedback) */
  correctAnswer: number;
  /** Whether we're in the answer-reveal phase (shows correct/incorrect styling) */
  showResult: boolean;
  /** Called when the player taps an answer option */
  onSelectAnswer: (index: number) => void;
  /** Whether buttons are disabled (during reveal or between turns) */
  disabled: boolean;
  /** Index of an incorrect answer eliminated by Teacher's Desk help (MC only) */
  eliminatedOption?: number | null;
}

/** Letter labels for multiple-choice options */
const OPTION_LETTERS = ['A', 'B', 'C', 'D'];

/** CSS class names for each button's 3D gradient (from chalkboard-textures.css) */
const BUTTON_3D_CLASSES = ['btn-3d-a', 'btn-3d-b', 'btn-3d-c', 'btn-3d-d'];

export function AnswerButtons({
  options,
  questionType,
  selectedAnswer,
  correctAnswer,
  showResult,
  onSelectAnswer,
  disabled,
  eliminatedOption = null,
}: AnswerButtonsProps) {
  const isTrueFalse = questionType === 'true-false';

  /** Build inline overrides for result-reveal state feedback */
  function getResultOverrides(index: number): React.CSSProperties {
    if (!showResult) {
      /* Pre-reveal: highlight selected answer with gold border */
      if (selectedAnswer === index) {
        return { border: '2px solid var(--accent-gold)' };
      }
      return {};
    }

    /* Post-reveal states */
    if (index === correctAnswer) {
      return {
        border: '2px solid var(--accent-gold)',
        boxShadow: '0 0 12px rgba(74, 103, 65, 0.4), 0 4px 0 rgba(0,0,0,0.3)',
      };
    }
    if (index === selectedAnswer) {
      return { opacity: 0.75, filter: 'saturate(0.6)' };
    }
    return { opacity: 0.45 };
  }

  return (
    <div
      style={isTrueFalse ? styles.trueFalseGrid : styles.grid}
      role="group"
      aria-label="Answer options"
    >
      {options.map((option, index) => {
        const isEliminated = eliminatedOption === index;

        return (
          <button
            key={index}
            className={`btn-3d ${BUTTON_3D_CLASSES[index] ?? BUTTON_3D_CLASSES[0]}`}
            style={{
              ...styles.button,
              ...(isTrueFalse ? styles.trueFalseButton : {}),
              ...getResultOverrides(index),
              ...(isEliminated ? styles.eliminated : {}),
            }}
            onClick={() => onSelectAnswer(index)}
            disabled={disabled || isEliminated}
            aria-label={`${isTrueFalse ? '' : `Option ${OPTION_LETTERS[index]}: `}${option}${isEliminated ? ' (eliminated)' : ''}`}
          >
            {!isTrueFalse && (
              <span style={styles.letter}>{OPTION_LETTERS[index]}</span>
            )}
            <span
              style={{
                ...styles.optionText,
                ...(isEliminated ? styles.eliminatedText : {}),
              }}
            >
              {option}
            </span>
          </button>
        );
      })}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.75rem',
    width: '100%',
    maxWidth: '440px',
    margin: '0 auto',
    padding: '0 0.5rem',
  },
  trueFalseGrid: {
    display: 'flex',
    gap: '0.75rem',
    justifyContent: 'center',
    width: '100%',
    maxWidth: '440px',
    margin: '0 auto',
    padding: '0 0.5rem',
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.875rem 1rem',
    color: 'var(--text-light)',
    fontWeight: '600',
    fontSize: '1.05rem',
    fontFamily: 'var(--font-family-handwriting)',
    minHeight: '56px',
    border: '2px solid transparent',
    transition: 'transform 0.1s ease, box-shadow 0.1s ease, opacity 0.2s ease',
  },
  trueFalseButton: {
    flex: '1',
    maxWidth: '200px',
    justifyContent: 'center',
    fontSize: '1.15rem',
  },
  letter: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    fontWeight: '700',
    fontSize: '0.9rem',
    fontFamily: 'var(--font-family)',
    flexShrink: 0,
  },
  optionText: {
    flex: 1,
    textAlign: 'left' as const,
    lineHeight: '1.3',
  },
  /* Eliminated answer — faded out and struck through by Teacher's Desk help */
  eliminated: {
    opacity: 0.3,
    filter: 'saturate(0.3)',
    cursor: 'default',
    transform: 'scale(0.97)',
  },
  eliminatedText: {
    textDecoration: 'line-through',
  },
};
